import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../store/useUser';
import { getMedcallAccess, isMedcallPath } from '@/pages/medcall/medcallAccess';
import { menuItems } from '../../layout/menuItems';
import { useMutation, useQuery, gql } from '@apollo/client';
import { SearchComponent } from '@/components/ui/search';
import {
  ArrowRight,
  LucideIcon,
  Mail,
  Home,
  Search,
  Trash2,
  UserPlus,
  AlertCircle,
  AlertTriangle,
  Info,
  History,
  ClipboardList,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { YesNoDialog } from '@/components/ui/modules/yes-no-dialog';
import { getUserPolicyList, policyMatches } from '@/lib/policies';
import { toast } from '@/components/ui/toast';
import { CreateUserDialog, CreateUserDialogValues } from '@/pages/admin/userAccounts/components/CreateUserDialog';
import { buildLegacyAwareAssignments } from '@/pages/admin/userAccounts/legacyPolicyAssignments';
import { LegacyDashboardSection } from '@/pages/legacy/LegacyDashboardSection';
import './dashboard.css';

const ADMIN_DASHBOARD_LABELS = [
  'Provision Users',
  'Password Resets',
  'Two-Factor Enrollment',
];

// GraphQL Queries
const GET_RECENT_MESSAGES = gql`
  query GetRecentMessages($limit: Int) {
    listUserInboxMessages(limit: $limit, offset: 0, filter: "inbox:unread") {
      messages {
        id
        senderString
        subject
        createdAt
        isRead
      }
      totalCount
    }
  }
`;

const GET_DASHBOARD_ANNOUNCEMENTS = gql`
  query GetDashboardAnnouncements {
    getDashboardNews {
      enabled
      title
      content
      status
    }
  }
`;

const GET_PARTICIPANTS_BY_ODR_NUMBERS = gql`
  query GetParticipantsByODRNumber($odrnumbers: [Int]) {
    getParticipantsByODRNumber(odrnumbers: $odrnumbers) {
      id
      name {
        firstName
        lastName
      }
    }
  }
`;

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      name
      item
      dueInDays
      nextDueDate
      status
      required
    }
  }
`;

const GET_DASHBOARD_TODOS = gql`
  query DashboardTodos($limit: Int, $offset: Int) {
    dashboardTodos(limit: $limit, offset: $offset) {
      id
      todoType
      title
      summary
      detail
      status
      createdAt
      ticketId
      requestId
      targetPath
      requestType
      requesterName
      requestedName
      emailAddress
      organizationName
      programName
      roleName
      accessReason
      username
      message
    }
  }
`;

const CONVERT_USER_ACCOUNT_REQUEST = gql`
  mutation DashboardConvertUserAccountRequest($input: ConvertUserAccountRequestInput!) {
    convertUserAccountRequest(input: $input) {
      success
      message
      requestId
      createdUserId
      username
      temporaryPassword
    }
  }
`;

const CANCEL_USER_ACCOUNT_REQUEST = gql`
  mutation DashboardCancelUserAccountRequest($input: CancelUserAccountRequestInput!) {
    cancelUserAccountRequest(input: $input) {
      success
      message
      requestId
    }
  }
`;

// Types
interface MenuItem {
  label: string;
  path: string;
  hardlink?: boolean;
  icon?: LucideIcon;
}

interface MenuSection {
  label: string;
  icon?: LucideIcon;
  submenu: MenuItem[];
}

interface ParticipantSummary {
  id: string | number | Array<string | number>;
  name?: {
    firstName: string;
    lastName: string;
  };
}

interface DashboardTodoItem {
  id: number;
  todoType: 'support_ticket' | 'user_account_request' | 'central_planner';
  title?: string;
  summary?: string;
  detail?: string;
  status?: string;
  createdAt?: string;
  ticketId?: number | null;
  requestId?: number | null;
  targetPath?: string | null;
  requestType?: string;
  requesterName?: string;
  requestedName?: string;
  emailAddress?: string;
  organizationName?: string;
  programName?: string;
  roleName?: string;
  accessReason?: string;
  username?: string;
  message?: string;
}

type ConvertUserAccountResponse = {
  convertUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
    createdUserId?: number;
    username?: string;
    temporaryPassword?: string | null;
  };
};

type CancelUserAccountResponse = {
  cancelUserAccountRequest?: {
    success?: boolean;
    message?: string;
    requestId?: number;
  };
};

const clean = (value: string) => `${value || ''}`.trim();
const getRecentClientId = (id: ParticipantSummary['id']) => {
  if (Array.isArray(id)) return clean(`${id[0] ?? ''}`);
  return clean(`${id ?? ''}`);
};
const normalizeTodoToken = (value?: string) => clean(`${value || ''}`).toLowerCase().replace(/\s+/g, ' ');
const normalizeTodoProgram = (value?: string) => {
  const normalized = normalizeTodoToken(value);
  if (normalized === 'fist') return 'FIST';
  if (normalized === 'mist') return 'MIST';
  if (normalized === 'odrh') return 'ODRH';
  if (normalized === 'maternal health') return 'Maternal Health';
  if (normalized === 'lead') return 'LEAD';
  return '';
};

const buildTodoDefaultPolicies = (request?: DashboardTodoItem | null) => {
  if (!request) return [];
  const normalizedProgram = normalizeTodoProgram(request.programName);

  const base = ['User'];
  const programMap: Record<string, string> = {
    FIST: 'legacy:fistaccess',
    MIST: 'legacy:diversionaccess',
    ODRH: 'legacy:odrhousingaccess',
    'Maternal Health': 'legacy:prgwmnaccess',
    LEAD: 'legacy:leadaccess'
  };
  const programPolicy = programMap[normalizedProgram];
  if (programPolicy) base.push(programPolicy);
  return Array.from(new Set(base));
};

const suggestUsername = (request: DashboardTodoItem) => {
  const email = clean(request.emailAddress || '').toUpperCase();
  if (email.includes('@')) {
    const local = email.split('@')[0];
    const normalized = local.replace(/[^A-Z0-9._-]/g, '');
    if (normalized) return normalized.slice(0, 36);
  }

  const fromName = clean(request.requestedName || '').toUpperCase().replace(/[^A-Z0-9._-]/g, '');
  if (fromName) return fromName.slice(0, 36);
  return '';
};

type DashboardActionRowProps = {
  title: string;
  subtitle?: string;
  meta?: string;
  onClick: () => void;
  actionLabel?: string | null;
  showArrow?: boolean;
  metaOnTop?: boolean;
};

const DashboardActionRow = ({
  title,
  subtitle,
  meta,
  onClick,
  actionLabel = 'Open',
  showArrow = true,
  metaOnTop = false,
}: DashboardActionRowProps) => {
  const showTrailing = Boolean(actionLabel) || showArrow || (!metaOnTop && meta);

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="dashboard-action-row group h-auto justify-between px-3 py-2 font-normal text-[var(--neutral-gray-120)] hover:bg-[var(--neutral-gray-10)] hover:text-[var(--neutral-gray-120)]"
      onClick={onClick}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="app-list-item-title dashboard-text-primary line-clamp-1">{title}</p>
          {metaOnTop && meta && (
            <span className="dashboard-meta whitespace-nowrap">{meta}</span>
          )}
        </div>
        {subtitle && (
          <p className="app-list-item-subtitle mt-0.5 line-clamp-1">{subtitle}</p>
        )}
      </div>
      {showTrailing && (
        <div className="ml-3 flex shrink-0 items-center gap-2">
          {!metaOnTop && meta && <span className="dashboard-meta whitespace-nowrap">{meta}</span>}
          {actionLabel && <span className="dashboard-row-action">{actionLabel}</span>}
          {showArrow && <ArrowRight className="h-4 w-4 text-[var(--azure-primary)] transition-colors group-hover:text-[var(--azure-primary-hover)]" />}
        </div>
      )}
    </Button>
  );
};

export const Dashboard = () => {
  const { user, recentParticipants } = useUser();
  const navigate = useNavigate();
  const [dashboardSearchDraft, setDashboardSearchDraft] = useState('');
  const [dashboardSearchQuery, setDashboardSearchQuery] = useState<string | undefined>(undefined);
  const [selectedTodoRequest, setSelectedTodoRequest] = useState<DashboardTodoItem | null>(null);
  const [isTodoCreateDialogOpen, setIsTodoCreateDialogOpen] = useState(false);
  const [pendingDeleteRequestId, setPendingDeleteRequestId] = useState<number | null>(null);
  
  // Fetch dashboard data
  const { data: messagesData, loading: messagesLoading } = useQuery(GET_RECENT_MESSAGES, {
    variables: { limit: 5 },
    skip: !user,
    errorPolicy: 'ignore'
  });

  const { data: announcementsData } = useQuery(GET_DASHBOARD_ANNOUNCEMENTS, {
    skip: !user,
    errorPolicy: 'ignore'
  });
  const showAnnouncements = Boolean(announcementsData?.getDashboardNews?.enabled);

  const { data: complianceData, loading: complianceLoading } = useQuery(GET_USER_COMPLIANCE_STATUS, {
    skip: !user,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });

  const policyList = useMemo(() => {
    return getUserPolicyList(user);
  }, [user?.policies]);
  const wildcard = policyList.includes('*:*~*');

  const canAccessPolicies = (requiredPolicies: string[]) => {
    if (wildcard) return true;
    if (policyList.length === 0) return false;
    return requiredPolicies.some((required) => policyList.some((policy) => policyMatches(policy, required)));
  };

  const canReadParticipants = canAccessPolicies(['program:participant~read']);

  // Recent Participants Data
  const recentParticipantIds = useMemo(() => {
    if (!Array.isArray(recentParticipants) || recentParticipants.length === 0) {
      return [];
    }

    const ids = recentParticipants
      .map((entry) => entry?.odrNumber)
      .filter((id): id is number => typeof id === "number");

    return Array.from(new Set(ids)).slice(0, 10);
  }, [recentParticipants]);

  const { data: recentClientsData, loading: recentClientsLoading, error: recentClientsError } = useQuery(GET_PARTICIPANTS_BY_ODR_NUMBERS, {
    variables: { odrnumbers: recentParticipantIds },
    skip: !canReadParticipants || recentParticipantIds.length === 0,
    errorPolicy: 'ignore'
  });
  const recentClients: ParticipantSummary[] = Array.isArray(recentClientsData?.getParticipantsByODRNumber)
    ? recentClientsData.getParticipantsByODRNumber.filter((client: any): client is ParticipantSummary => Boolean(getRecentClientId(client?.id)))
    : [];

  const showTrainingBanner = useMemo(() => {
    if (complianceLoading) {
      return false;
    }

    const items = complianceData?.getUserComplianceStatus;
    if (!Array.isArray(items)) {
      return false;
    }

    const requiredItems = items.filter((item: any) => item?.required);
    if (requiredItems.length === 0) {
      return false;
    }

    const overdue = requiredItems.some((item: any) => typeof item?.dueInDays === 'number' && item.dueInDays < 0);
    if (overdue) {
      return true;
    }

    const missingDates = requiredItems.some((item: any) => item?.dueInDays === null || item?.dueInDays === undefined);
    if (missingDates) {
      return true;
    }

    const dueSoon = requiredItems.some((item: any) => typeof item?.dueInDays === 'number' && item.dueInDays <= 45);
    return dueSoon;
  }, [complianceData, complianceLoading]);

  const upcomingTraining = useMemo(() => {
    const items = complianceData?.getUserComplianceStatus;
    if (!Array.isArray(items)) {
      return null;
    }

    const requiredItems = items.filter((item: any) => item?.required);
    if (requiredItems.length === 0) {
      return null;
    }

    const overdue = requiredItems.filter((item: any) => typeof item?.dueInDays === 'number' && item.dueInDays < 0);
    if (overdue.length > 0) {
      return { ...overdue[0], statusColor: 'red', label: 'Training Overdue' };
    }

    const dueSoon = requiredItems.filter((item: any) => typeof item?.dueInDays === 'number' && item.dueInDays <= 45);
    if (dueSoon.length > 0) {
      return { ...dueSoon.sort((a: any, b: any) => (a.dueInDays ?? 0) - (b.dueInDays ?? 0))[0], statusColor: 'amber', label: 'Training Due Soon' };
    }

    return null;
  }, [complianceData]);

  const canAccessTodoPolicies = (requiredPolicies: string[]) => {
    if (wildcard) return true;
    if (policyList.length === 0) return false;

    return requiredPolicies.some((required) =>
      policyList.some((policy) => {
        const normalizedPolicy = `${policy || ''}`.trim().toLowerCase();
        const isTodoScopedPolicy =
          normalizedPolicy === 'user:authenticated~full' ||
          normalizedPolicy === 'tools:support:tickets~full' ||
          normalizedPolicy === 'tools:requestuser~full' ||
          normalizedPolicy.startsWith('todo:') ||
          normalizedPolicy === '*:*~*' ||
          normalizedPolicy === 'system:fullaccess';
        if (!isTodoScopedPolicy) {
          return false;
        }
        return policyMatches(policy, required);
      })
    );
  };

  const canViewPlannerTodos = policyList.some(policy => policyMatches(policy, 'tools:centralplanner~read'));
  const canViewSpecificDashboardTodos = canViewPlannerTodos || canAccessTodoPolicies([
    'tools:support:tickets~full',
    'tools:requestuser~full',
    'tools:requestuser~full'
  ]);

  const { data: dashboardTodosData, loading: dashboardTodosLoading, refetch: refetchDashboardTodos } = useQuery(GET_DASHBOARD_TODOS, {
    variables: {
      limit: 500,
      offset: 0
    },
    skip: !user || !canViewSpecificDashboardTodos,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore'
  });

  const dashboardTodos: DashboardTodoItem[] = Array.isArray(dashboardTodosData?.dashboardTodos)
    ? dashboardTodosData.dashboardTodos
    : [];

  const todosLoading = Boolean(user) && canViewSpecificDashboardTodos && dashboardTodosLoading;
  const todoCount = dashboardTodos.length;

  const [convertUserAccountRequest, { loading: creatingUserFromRequest }] = useMutation<ConvertUserAccountResponse>(CONVERT_USER_ACCOUNT_REQUEST);
  const [cancelUserAccountRequest, { loading: deletingUserRequest }] = useMutation<CancelUserAccountResponse>(CANCEL_USER_ACCOUNT_REQUEST);

  const canCreateFromUserRequest = canAccessPolicies(['admin:access~full', 'admin:access~full', 'admin:access~full']);
  const canDeleteUserRequest = canAccessPolicies(['tools:requestuser~full']);

  const programToolSections = useMemo<MenuSection[]>(() => {
    if (!user) return [];
    const sectionPriority: Record<string, number> = {
      Administration: 0,
      'Candidates/Census': 1,
      Vaccines: 2,
      Billing: 3,
      Tools: 4,
    };

    const dedupeSubmenu = (items: MenuItem[]) => {
      const seen = new Set<string>();
      const uniqueItems: MenuItem[] = [];
      for (const item of items) {
        const key = `${item.label}|${item.path}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        uniqueItems.push(item);
      }
      return uniqueItems;
    };

    const resolveRequired = (item: any): string[] => {
      if (Array.isArray(item?.policies) && item.policies.length > 0) return item.policies;
      if (Array.isArray(item?.group) && item.group.length > 0) return item.group;
      if (Array.isArray(item?.access) && item.access.length > 0) return item.access;
      return [];
    };

    const canAccessMenuItem = (item: any): boolean => {
      if (!user) return false;
      if (isMedcallPath(item?.path)) return getMedcallAccess(user) !== 'none';
      if (item?.anyAuthenticated) return true;
      const required = resolveRequired(item);
      if (wildcard) return true;
      if (required.length === 0) {
        return typeof item?.path !== 'string';
      }
      if (policyList.length === 0) return false;
      return required.some((req) => policyList.some((pol) => policyMatches(pol, req)));
    };

    const administrationRoot = menuItems.find((menuItem: any) => menuItem.type === 'item' && menuItem.label === 'Administration');
    const adminSectionItems = ADMIN_DASHBOARD_LABELS
      .map((label) => menuItems.find((menuItem: any) => menuItem.type === 'item' && menuItem.label === label))
      .filter(Boolean)
      .filter((menuItem: any) => canAccessMenuItem(menuItem));

    const sections: MenuSection[] = [];

    if (administrationRoot && canAccessMenuItem(administrationRoot)) {
      sections.push({
        label: 'Administration',
        icon: administrationRoot.icon,
        submenu: dedupeSubmenu([
          {
            label: 'Admin Center',
            path: administrationRoot.path,
            icon: administrationRoot.icon
          },
          ...adminSectionItems,
        ]),
      });
    }

    const programSections = menuItems
      .filter((menuItem: any) =>
        menuItem.type === 'separator' &&
        Array.isArray(menuItem.submenu) &&
        String(menuItem.label || '').trim() !== '')
      .filter((menuItem: any) => canAccessMenuItem(menuItem))
      .map((menuItem: any) => {
        const accessibleSubmenu = (menuItem.submenu as MenuItem[]).filter((subItem: any) => canAccessMenuItem(subItem));
        return {
          label: menuItem.label,
          icon: menuItem.icon,
          submenu: dedupeSubmenu(accessibleSubmenu),
        };
      })
      .sort((left: MenuSection, right: MenuSection) => {
        const leftPriority = sectionPriority[left.label] ?? 99;
        const rightPriority = sectionPriority[right.label] ?? 99;
        if (leftPriority !== rightPriority) return leftPriority - rightPriority;
        return 0;
      });

    return [...sections, ...programSections].filter((section: MenuSection) => section.submenu.length > 0);
  }, [policyList, user, wildcard]);

  const showProgramToolSection = programToolSections.length > 0;
  const showTodoSection = canViewSpecificDashboardTodos && (todosLoading || todoCount > 0);

  const getProgramSectionClassName = (label: string) => {
    if (programToolSections.length === 1) {
      return 'dashboard-program-section dashboard-program-section--full';
    }
    if (label === 'Candidates/Census') return 'dashboard-program-section dashboard-program-section--census';
    if (label === 'Vaccines') return 'dashboard-program-section dashboard-program-section--vaccines';
    if (label === 'Tools') return 'dashboard-program-section dashboard-program-section--tools';
    return 'dashboard-program-section';
  };

  const openMenuPath = (item: MenuItem) => {
    if (item.hardlink) {
      window.open(item.path, '_blank');
      return;
    }

    navigate(item.path);
  };

  const submitDashboardSearch = () => {
    const nextQuery = dashboardSearchDraft.trim();
    if (!nextQuery) {
      return;
    }

    setDashboardSearchQuery(nextQuery);
  };

  // Format relative time
  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const truncateText = (value: string, max = 100) => {
    const text = `${value || ''}`;
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1)}...`;
  };

  const getTodoSubtitle = (todo: DashboardTodoItem) => {
    const content = [todo.summary, todo.detail].filter(Boolean).join(' · ');
    return truncateText(content, 120);
  };

  const openDashboardTodo = (todo: DashboardTodoItem) => {
    if (todo.todoType === 'user_account_request') {
      setSelectedTodoRequest(todo);
      return;
    }

    const targetPath = todo.targetPath || (todo.ticketId ? `/admin/help?ticketId=${todo.ticketId}` : '');
    if (!targetPath) return;
    navigate(targetPath);
  };

  const reloadDashboardTodos = async () => {
    if (!user) return;
    await refetchDashboardTodos();
  };
  
  const handleCreateFromTodoRequest = async (values: CreateUserDialogValues) => {
    if (!selectedTodoRequest) {
      toast.error('Select a request first');
      return;
    }

    try {
      const requestId = selectedTodoRequest.requestId || selectedTodoRequest.id;
      const response = await convertUserAccountRequest({
        variables: {
          input: {
            requestId,
            username: values.username,
            emailAddress: values.emailAddress,
            organization: values.organization || null,
            site: values.site || null,
            legacyAccesses: values.legacyAccesses,
            selectedPolicies: values.selectedPolicies,
            activateNow: true
          }
        }
      });

      const result = response?.data?.convertUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to create user');
        return;
      }

      const username = result.username || values.username;
      toast.success(`Created user ${result.username || username}`);
      setSelectedTodoRequest(null);
      setIsTodoCreateDialogOpen(false);
      await reloadDashboardTodos();

    } catch (error: any) {
      toast.error(error?.message || 'Failed to create user');
    }
  };

  const handleDeleteTodoRequest = async () => {
    if (!pendingDeleteRequestId) return;
    try {
      const response = await cancelUserAccountRequest({
        variables: {
          input: {
            requestId: pendingDeleteRequestId,
            cancelReason: 'Deleted from dashboard pending user requests'
          }
        }
      });
      const result = response?.data?.cancelUserAccountRequest;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to delete request');
        return;
      }

      toast.success(`Deleted request #${pendingDeleteRequestId}`);
      setPendingDeleteRequestId(null);
      if ((selectedTodoRequest?.requestId || selectedTodoRequest?.id) === pendingDeleteRequestId) {
        setSelectedTodoRequest(null);
        setIsTodoCreateDialogOpen(false);
      }
      await reloadDashboardTodos();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete request');
    }
  };

  const renderAnnouncementsCard = (compact = false) => {
    if (!showAnnouncements) return null;
    const ann = announcementsData?.getDashboardNews;
    if (!ann) return null;

    const raw = ann.status || 'warning';
    const tone = raw === 'alarm' ? 'red' : (raw === 'alert' ? 'yellow' : (raw === 'warning' ? 'yellow' : raw));

    const getStatusStyles = () => {
      if (tone === 'red') {
        return {
          cardCls: 'border border-[var(--semantic-error-border-soft)] bg-[var(--surface-panel-solid)]',
          iconWrapCls: 'bg-[var(--semantic-error-soft)] text-[var(--semantic-error)]',
          titleCls: 'dashboard-text-primary',
          textCls: 'dashboard-text-secondary',
          icon: AlertCircle
        };
      }
      if (tone === 'yellow') {
        return {
          cardCls: 'border border-[var(--semantic-warning-border-soft)] bg-[var(--surface-panel-solid)]',
          iconWrapCls: 'bg-[var(--semantic-warning-soft)] text-[var(--semantic-warning)]',
          titleCls: 'dashboard-text-primary',
          textCls: 'dashboard-text-secondary',
          icon: AlertTriangle
        };
      }
      return {
        cardCls: 'border border-[var(--azure-primary-border-soft)] bg-[var(--surface-panel-solid)]',
        iconWrapCls: 'bg-[var(--azure-primary-soft)] text-[var(--azure-primary)]',
        titleCls: 'dashboard-text-primary',
        textCls: 'dashboard-text-secondary',
        icon: Info
      };
    };

    const styles = getStatusStyles();
    const IconComponent = styles.icon;

    return (
      <Card className={`${styles.cardCls} ${compact ? 'dashboard-announcement-banner' : 'dashboard-panel'}`}>
        <CardContent className={compact ? 'px-5 py-4' : 'px-5 py-5'}>
          <div className="flex gap-3">
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${styles.iconWrapCls}`}>
              <IconComponent size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`${styles.titleCls} dashboard-card-heading mb-1.5`}>
                {ann.title}
              </h3>
              <div
                className={`${styles.textCls} dashboard-card-text prose prose-sm max-w-none`}
                dangerouslySetInnerHTML={{ __html: ann.content }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="dashboard-page flex h-full flex-col overflow-hidden">
      <main className="flex-1 overflow-y-auto px-2 py-5 sm:px-3 lg:px-4 xl:px-5">
        <div className="dashboard-shell space-y-4">
          {showTrainingBanner && (
            <div>
              {(() => {
                const bannerColor = upcomingTraining?.statusColor || 'red';
                const bannerCls = bannerColor === 'red'
                  ? 'border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)]'
                  : bannerColor === 'amber'
                    ? 'border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)]'
                    : 'border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)]';
                const iconCls = bannerColor === 'red'
                  ? 'text-[var(--semantic-error)]'
                  : bannerColor === 'amber'
                    ? 'text-[var(--semantic-warning)]'
                    : 'text-[var(--azure-primary)]';
                const bannerHeadline = upcomingTraining?.label || (bannerColor === 'amber' ? 'Training Due Soon' : 'Training Required');
                const bannerMessage = bannerColor === 'amber'
                  ? 'Required training is due soon. Complete it before the due date.'
                  : 'Complete required trainings before continuing.';

                return (
                  <div className={`dashboard-panel rounded-2xl border p-4 ${bannerCls}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`h-5 w-5 ${iconCls}`} />
                        <div>
                          <div className="dashboard-card-heading dashboard-text-primary">
                            {upcomingTraining?.name || bannerHeadline}
                          </div>
                          <div className="dashboard-card-text dashboard-text-secondary">
                            {bannerMessage}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate('/support/training')}
                        variant="default"
                        className="h-10"
                      >
                        View Trainings
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {showAnnouncements ? renderAnnouncementsCard(true) : null}
          <div className="dashboard-main-grid">
            <section className="dashboard-main-section dashboard-main-section--tools xl:h-full">
              {showProgramToolSection ? (
              <Card className="dashboard-panel dashboard-tools-card xl:flex xl:h-full xl:flex-col">
                <CardHeader className="border-b border-[var(--neutral-gray-20)] px-4 pb-2 pt-3 lg:px-5">
                  <div className="flex items-center gap-2">
                    <Home className="h-5 w-5 dashboard-icon-muted" />
                    <CardTitle className="dashboard-section-title text-base dashboard-text-primary">Programs & Tools</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 px-4 pb-4 pt-3 lg:px-5 xl:flex-1 xl:min-h-0">
                  <div className="dashboard-tools-scroll xl:flex-1 xl:min-h-0 xl:max-h-none">
                    <div className="dashboard-program-grid">
                      {programToolSections.map((section: MenuSection) => (
                        <div key={section.label} className={getProgramSectionClassName(section.label)}>
                          <div className="mb-1">
                            <h3 className="dashboard-card-heading dashboard-program-section-heading dashboard-text-primary">{section.label}</h3>
                          </div>
                          <div className="dashboard-program-items">
                            {section.submenu.map((item) => (
                              <DashboardActionRow
                                key={`${section.label}-${item.path}-${item.label}`}
                                title={item.label}
                                actionLabel={null}
                                showArrow={false}
                                onClick={() => openMenuPath(item)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                      <LegacyDashboardSection />
                    </div>
                  </div>
                </CardContent>
              </Card>
              ) : (
              <Card className="dashboard-panel dashboard-tools-card xl:flex xl:flex-col">
                <CardHeader className="border-b border-[var(--neutral-gray-20)] px-4 pb-2 pt-3 lg:px-5">
                  <div className="flex items-center gap-2">
                    <Search className="h-5 w-5 dashboard-icon-muted" />
                    <CardTitle className="dashboard-section-title text-base dashboard-text-primary">Search</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-col gap-2 px-4 pb-4 pt-3 lg:px-5 xl:min-h-0">
                  <p className="dashboard-card-text whitespace-nowrap dashboard-text-secondary">
                    Enter a name, CID, MRUN, FIN, or ChampID to search directly from the dashboard.
                  </p>
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitDashboardSearch();
                    }}
                  >
                    <Input
                      value={dashboardSearchDraft}
                      onChange={(event) => setDashboardSearchDraft(event.target.value)}
                      placeholder="Search by name, CID, MRUN, FIN, or ChampID"
                      className="h-9"
                    />
                    <Button type="submit" size="sm" className="h-9 shrink-0">
                      Search
                    </Button>
                  </form>
                  {dashboardSearchQuery ? (
                    <div className="min-h-0 flex-1 overflow-hidden">
                      <SearchComponent
                        onClose={() => {}}
                        onSearch={(id) => navigate(`/participants/${id}`)}
                        searchQuery={dashboardSearchQuery}
                      />
                    </div>
                  ) : null}
                  <LegacyDashboardSection />
                </CardContent>
              </Card>
              )}
            </section>

            <section className="dashboard-main-section dashboard-main-section--recent xl:self-start">
              <Card className="dashboard-panel xl:flex xl:flex-col">
                <CardHeader className="border-b border-[var(--neutral-gray-20)] px-4 pb-2 pt-3 lg:px-5">
                  <div className="flex items-center gap-2">
                    <History className="h-5 w-5 dashboard-icon-muted" />
                    <CardTitle className="dashboard-section-title text-base dashboard-text-primary">Recent Clients</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0 xl:flex xl:flex-1 xl:min-h-0 xl:flex-col">
                  {recentClientsLoading && (
                    <div className="dashboard-empty-state flex items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                      <p className="dashboard-card-text">Loading recent clients...</p>
                    </div>
                  )}

                  {recentClientsError && !recentClientsLoading && (
                    <div className="dashboard-empty-state flex items-center justify-center px-6 text-center dashboard-error-text xl:flex-1">
                      <p className="dashboard-card-text">Error loading recent clients.</p>
                    </div>
                  )}

                  {!recentClientsLoading && !recentClientsError && recentClients.length > 0 && (
                    <div className="dashboard-recent-clients-grid p-3 xl:flex-1 xl:min-h-0 xl:max-h-none">
                      {recentClients.map((client: ParticipantSummary) => {
                        const clientId = getRecentClientId(client.id);

                        return (
                          <DashboardActionRow
                            key={clientId}
                            title={
                              [clean(client?.name?.firstName || ''), clean(client?.name?.lastName || '')]
                                .filter(Boolean)
                                .join(' ') || `Client ${clientId}`
                            }
                            meta={clientId || undefined}
                            actionLabel={null}
                            showArrow={false}
                            onClick={() => navigate(`/participants/${clientId}`)}
                          />
                        );
                      })}
                    </div>
                  )}

                  {!recentClientsLoading && !recentClientsError && recentClients.length === 0 && (
                    <div className="dashboard-empty-state flex items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                      <p className="dashboard-card-text">No recent clients yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            <div className="dashboard-side-column dashboard-main-section dashboard-main-section--side">
              {showTodoSection && (
              <section className="dashboard-todo-section">
                <Card className="dashboard-panel dashboard-list-card dashboard-todo-card">
                  <CardHeader className="border-b border-[var(--neutral-gray-20)] px-4 pb-2 pt-3 lg:px-5">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5 dashboard-icon-muted" />
                      <CardTitle className="dashboard-section-title text-base dashboard-text-primary">Todos</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 xl:flex xl:flex-1 xl:min-h-0 xl:flex-col">
                    {showTodoSection && todosLoading && (
                      <div className="dashboard-empty-state flex items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                        <p className="dashboard-card-text">Loading todos...</p>
                      </div>
                    )}
                    {showTodoSection && !todosLoading && todoCount === 0 && (
                      <div className="dashboard-empty-state flex flex-col items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                        <ClipboardList className="mb-3 h-8 w-8 dashboard-icon-subtle" />
                        <p className="dashboard-card-text">No open todos.</p>
                      </div>
                    )}
                    {showTodoSection && !todosLoading && todoCount > 0 && (
                      <div className="dashboard-list-scroll dashboard-todo-list-scroll p-3 xl:flex-1">
                        {dashboardTodos.map((todo) => (
                          <DashboardActionRow
                            key={`${todo.todoType}-${todo.id}`}
                            title={todo.title || (todo.todoType === 'user_account_request' ? `User Request #${todo.id}` : `Support Ticket #${todo.id}`)}
                            subtitle={getTodoSubtitle(todo)}
                            meta={todo.createdAt ? formatRelativeTime(todo.createdAt) : undefined}
                            actionLabel={null}
                            showArrow={false}
                            metaOnTop
                            onClick={() => openDashboardTodo(todo)}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
              )}

              <section className="xl:flex-1">
                <Card className="dashboard-panel dashboard-list-card xl:flex xl:h-full xl:flex-col">
                  <CardHeader className="border-b border-[var(--neutral-gray-20)] px-4 pb-2 pt-3 lg:px-5">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 dashboard-icon-muted" />
                      <CardTitle className="dashboard-section-title text-base dashboard-text-primary">Messages</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 xl:flex xl:flex-1 xl:min-h-0 xl:flex-col">
                    {messagesLoading && (
                      <div className="dashboard-empty-state flex items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                        <p className="dashboard-card-text">Loading messages...</p>
                      </div>
                    )}
                    {!messagesLoading && messagesData?.listUserInboxMessages?.messages?.length > 0 && (
                      <div className="dashboard-list-scroll p-3 xl:flex-1">
                        {messagesData.listUserInboxMessages.messages.map((message: any, index: number) => (
                          <DashboardActionRow
                            key={`${message.id ?? 'message'}-${index}`}
                            title={message.senderString || 'Unknown Sender'}
                            subtitle={truncateText(message.subject || 'No subject', 90)}
                            meta={formatRelativeTime(message.createdAt)}
                            actionLabel={null}
                            showArrow={false}
                            metaOnTop
                            onClick={() => navigate('/messages')}
                          />
                        ))}
                      </div>
                    )}
                    {!messagesLoading && !messagesData?.listUserInboxMessages?.messages?.length && (
                      <div className="dashboard-empty-state flex flex-col items-center justify-center px-6 text-center dashboard-text-secondary xl:flex-1">
                        <Mail className="mb-3 h-8 w-8 dashboard-icon-subtle" />
                        <p className="dashboard-card-text">No new messages.</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => navigate('/messages')}
                        >
                          Open Message Center
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>
            </div>
          </div>

        </div>
      </main>

      <Dialog
        open={selectedTodoRequest !== null}
        onOpenChange={(open: boolean) => {
          if (open) return;
          setSelectedTodoRequest(null);
        }}
      >
        <DialogContent className="w-[92vw] max-w-[760px] overflow-hidden border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] p-0 shadow-[var(--shadow-light)]">
          <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-5 pb-3 pt-5">
            <DialogTitle className="text-base font-semibold text-[var(--neutral-gray-120)]">
              {selectedTodoRequest ? `User Request #${selectedTodoRequest.id}` : 'User Request'}
            </DialogTitle>
          </DialogHeader>

          {selectedTodoRequest && (
            <div className="space-y-4 px-5 pb-5">
              <div className="rounded-md border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-3">
                <p className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                  {selectedTodoRequest.requestedName || 'Unknown User'}
                </p>
                <p className="mt-0.5 text-xs text-[var(--neutral-gray-100)]">
                  {selectedTodoRequest.emailAddress || 'No email'}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Request ID</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">#{selectedTodoRequest.id}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Status</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.status || 'pending'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Requester</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.requesterName || 'N/A'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Organization</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.organizationName || 'N/A'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Program</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.programName || 'N/A'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Role</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.roleName || 'N/A'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Request Type</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.requestType || 'activation'}</p>
                </div>
                <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Created</p>
                  <p className="mt-0.5 text-[var(--neutral-gray-120)]">{selectedTodoRequest.createdAt ? new Date(selectedTodoRequest.createdAt).toLocaleString() : 'N/A'}</p>
                </div>
              </div>

              <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-[var(--neutral-gray-80)]">Reason</p>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-[var(--neutral-gray-120)]">
                  {selectedTodoRequest.accessReason || 'No reason provided'}
                </p>
              </div>

              <DialogFooter className="border-t border-[var(--neutral-gray-30)] px-0 pt-3">
                {canDeleteUserRequest && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setPendingDeleteRequestId(selectedTodoRequest.id)}
                    disabled={deletingUserRequest}
                    className="sm:mr-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Request</span>
                  </Button>
                )}
                {canCreateFromUserRequest && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setIsTodoCreateDialogOpen(true)}
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Create User</span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedTodoRequest(null)}
                >
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreateUserDialog
        open={isTodoCreateDialogOpen}
        onOpenChange={setIsTodoCreateDialogOpen}
        onSubmit={handleCreateFromTodoRequest}
        submitting={creatingUserFromRequest}
        title={selectedTodoRequest ? `Create User from Request #${selectedTodoRequest.id}` : 'Create User'}
        description="Create a user account directly from this pending request."
        submitLabel="Create User"
        showRealNameField={false}
        contextPanel={selectedTodoRequest ? (
          <div className="space-y-2 text-sm text-[var(--neutral-gray-100)]">
            <div><strong>Request:</strong> #{selectedTodoRequest.id}</div>
            <div><strong>Requested User:</strong> {selectedTodoRequest.requestedName || 'N/A'}</div>
            <div><strong>Requester:</strong> {selectedTodoRequest.requesterName || 'N/A'}</div>
            <div><strong>Program:</strong> {selectedTodoRequest.programName || 'N/A'}</div>
            <div><strong>Role:</strong> {selectedTodoRequest.roleName || 'N/A'}</div>
          </div>
        ) : null}
        initialValues={selectedTodoRequest ? {
          emailAddress: selectedTodoRequest.emailAddress || '',
          organization: selectedTodoRequest.organizationName || '',
          roleName: selectedTodoRequest.roleName || '',
          legacyAccesses: buildLegacyAwareAssignments(buildTodoDefaultPolicies(selectedTodoRequest)).legacyPermissions.map((entry) => entry.permission),
          selectedPolicies: buildTodoDefaultPolicies(selectedTodoRequest)
        } : undefined}
      />

      <YesNoDialog
        open={pendingDeleteRequestId !== null}
        onOpenChange={(open: boolean) => {
          if (open) return;
          setPendingDeleteRequestId(null);
        }}
        title="Delete User Request"
        description={`Delete request #${pendingDeleteRequestId || ''}?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteTodoRequest}
        confirmDisabled={deletingUserRequest || !pendingDeleteRequestId}
      />
    </div>
  );
};
